import { IdleWarningModalTemplate } from "./idle-warning-modal-template";

type IdleConfig = {
  idleTimeMs: number;
  responseTimeMs: number;
  onLogout: () => void;
};

export class UserIdleManager {
  private idleTimer: any;
  private responseTimer: any;
  private countdownInterval: any;
  private warningDeadline = 0;
  private lastActivityTime = Date.now();
  private isPopupVisible = false;
  private static readonly MINUTE_IN_MS = 60 * 1000;

  constructor(private config: IdleConfig) {}

  public start() {
    this.initialize();
  }

  private initialize() {
    this.initActivityListeners();
    this.startIdleTimer();
  }

  private initActivityListeners() {
    const events = ["mousemove", "keydown", "click", "scroll"];

    events.forEach((event) =>
      window.addEventListener(event, this.resetTimer.bind(this))
    );
  }

  private resetTimer() {
    if (this.isPopupVisible) return;

    this.lastActivityTime = Date.now();

    clearTimeout(this.idleTimer);
    this.startIdleTimer();
  }

  private startIdleTimer() {
    this.idleTimer = setTimeout(() => {
      this.showIdlePopup();
    }, this.config.idleTimeMs);
  }

  private showIdlePopup() {
    if (this.isPopupVisible) return;

    this.isPopupVisible = true;

    const popup = this.createPopup();
    document.body.appendChild(popup);
    document.body.classList.add("idle-warning-open");
    this.warningDeadline = Date.now() + this.config.responseTimeMs;

    this.startCountdown(popup);

    this.responseTimer = setTimeout(() => {
      this.logout();
    }, this.config.responseTimeMs);
  }

  private createPopup(): HTMLElement {
    const overlay = document.createElement("div");
    overlay.className = "idle-session-overlay";
    overlay.innerHTML = this.getModalTemplate();

    const continueButton = overlay.querySelector(
      '[data-idle-action="continue"]'
    );
    const logoutButton = overlay.querySelector('[data-idle-action="logout"]');

    continueButton?.addEventListener("click", () =>
      this.handleContinue(overlay)
    );
    logoutButton?.addEventListener("click", () => this.logout());

    return overlay;
  }

  private startCountdown(popup: HTMLElement) {
    this.updateCountdownText(popup);

    this.countdownInterval = setInterval(() => {
      this.updateCountdownText(popup);
    }, 1000);
  }

  private updateCountdownText(popup: HTMLElement) {
    const minutesContainer = popup.querySelector("[data-idle-minutes]");
    if (!minutesContainer) return;

    const remainingMinutes = Math.max(
      1,
      Math.ceil(
        (this.warningDeadline - Date.now()) / UserIdleManager.MINUTE_IN_MS
      )
    );

    minutesContainer.textContent = `${remainingMinutes} minute${
      remainingMinutes === 1 ? "" : "s"
    }`;
  }

  private getModalTemplate(): string {
    return IdleWarningModalTemplate.getHtml();
  }

  private handleContinue(popup: HTMLElement) {
    clearTimeout(this.responseTimer);
    clearInterval(this.countdownInterval);
    this.isPopupVisible = false;
    this.lastActivityTime = Date.now();

    popup.remove();
    document.body.classList.remove("idle-warning-open");

    clearTimeout(this.idleTimer);
    this.startIdleTimer();
  }

  private logout() {
    clearTimeout(this.responseTimer);
    clearTimeout(this.idleTimer);
    clearInterval(this.countdownInterval);
    document.body.classList.remove("idle-warning-open");
    this.warningDeadline = 0;

    const activePopup = document.querySelector(".idle-session-overlay");
    activePopup?.remove();
    this.isPopupVisible = false;

    this.config.onLogout();
  }
}
